// src/components/Students/StudentList.jsx
import React, { useState, useEffect, useRef } from "react";
import { Avatar } from "primereact/avatar";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { InputText } from "primereact/inputtext";
import { TabView, TabPanel } from "primereact/tabview";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Calendar } from "primereact/calendar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { toast } from "react-toastify";

import DynamicDataTable from "../../common/components/Common/DataListing";
import { printEnrollmentSlip } from "../../common/utils/printEnrollmentSlip";
import { useAuth } from "../../common/contexts/AuthContext";

import {
  getStudents,
  addStudent,
  editStudent,
  deleteStudent,
  uploadStudentPhoto,
} from "../../api/studentApi";
import { addUserWithStudent, getUsersByRole, quickCreateParent } from "../../common/api/userApi";
import { getRoles } from "../../common/api/roleApi";
import { getGrades } from "../../api/gradeApi";
import {
  getSubjectsByGrade,
  getStudentEnrollmentHistory,
} from "../../api/enrollmentApi";

const StudentList = () => {
  const { user } = useAuth() || {};
  const roleName = user?.role?.name || user?.Role?.name;
  const isParent = roleName === "Parent";

  const [studentsData, setStudentsData] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [mandatorySubjects, setMandatorySubjects] = useState([]);
  const [electiveSubjects, setElectiveSubjects] = useState([]);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [studentData, setStudentData] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [errors, setErrors] = useState({});

  // Inline "create parent on the go" dialog
  const [showParentDialog, setShowParentDialog] = useState(false);
  const [parentTarget, setParentTarget] = useState("parent_id"); // which guardian slot to fill
  const [savingParent, setSavingParent] = useState(false);
  const [newParent, setNewParent] = useState({ name: "", email: "", password: "" });

  // All dialog tabs share this content height so switching tabs doesn't shift the layout
  const TAB_CONTENT_HEIGHT = 460;

  // Minimum admission age (years) — birth date must be at least this far in the past
  const MIN_ADMISSION_AGE = 3;
  const today = new Date();
  const maxBirthDate = new Date(
    today.getFullYear() - MIN_ADMISSION_AGE,
    today.getMonth(),
    today.getDate()
  );
  const minBirthDate = new Date(
    today.getFullYear() - 25,
    today.getMonth(),
    today.getDate()
  );

  const [createUser, setCreateUser] = useState(false);
  const [roles, setRoles] = useState([]);
  const [parentUsers, setParentUsers] = useState([]);
  const [userFields, setUserFields] = useState({
    name: "",
    email: "",
    password: "",
    roleId: null,
  });

  const [enrollmentData, setEnrollmentData] = useState({
    id: null,
    enrollment_no: null,
    grade_id: null,
    subject_ids: [],
    status: "active",
    enrolled_at: new Date(),
    session_id: 1,
  });
  const [enrollmentsList, setEnrollmentsList] = useState([]);
  const [viewEnrollment, setViewEnrollment] = useState(null); // row shown in the read-only dialog
  const [editingEnrollmentNo, setEditingEnrollmentNo] = useState(null); // highlights the form when editing from history
  const enrollmentFormRef = useRef(null);

  // ---------- STUDENT PHOTO ----------
  const [photoFile, setPhotoFile] = useState(null); // pending file, uploaded on Save
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const photoInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const setPendingPhoto = (file) => {
    setPhotoFile(file);
    setPhotoPreview(file ? URL.createObjectURL(file) : null);
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
      });
      streamRef.current = stream;
      setIsCameraOpen(true);
    } catch {
      toast.error("Could not access camera. Check browser permission.");
    }
  };

  useEffect(() => {
    if (isCameraOpen && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
    if (!isCameraOpen) stopCamera();
    return stopCamera;
  }, [isCameraOpen]);

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        setPendingPhoto(new File([blob], `student-${Date.now()}.jpg`, { type: "image/jpeg" }));
        setIsCameraOpen(false);
      },
      "image/jpeg",
      0.9
    );
  };

  const maybeUploadPhoto = async (studentId) => {
    if (!photoFile || !studentId) return;
    try {
      await uploadStudentPhoto(studentId, photoFile);
    } catch (err) {
      console.error("Photo upload failed:", err);
      toast.warn("Student saved, but the photo upload failed.");
    }
  };
  const [currentStudentId, setCurrentStudentId] = useState(null);

  // -------- INITIAL LOAD --------
  useEffect(() => {
    const init = async () => {
      await fetchGrades();
      await fetchRoles();
      await fetchParentUsers();
      await fetchStudents();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- FETCH ----------
  const fetchGrades = async () => {
    try {
      const resp = await getGrades();
      const data = Array.isArray(resp) ? resp : resp.data || [];
      setGrades(data);
    } catch (err) {
      console.error("fetchGrades error:", err);
      setGrades([]);
    }
  };

  const fetchRoles = async () => {
    try {
      const resp = await getRoles();
      const data = Array.isArray(resp) ? resp : resp.data || [];
      setRoles(data);
    } catch (err) {
      console.error("fetchRoles error:", err);
      setRoles([]);
    }
  };

  const fetchParentUsers = async () => {
    if (isParent) return; // parents don't need this list
    try {
      // Role ID 6 = Parent
      const data = await getUsersByRole(6);
      setParentUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("fetchParentUsers error:", err);
      setParentUsers([]);
    }
  };

  // ---------- CREATE PARENT ON THE GO ----------
  const openParentDialog = (target) => {
    setParentTarget(target);
    setNewParent({ name: "", email: "", password: "" });
    setShowParentDialog(true);
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
    const bytes = new Uint32Array(14);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    const pwd = Array.from(bytes, (b) => chars[b % chars.length]).join("");
    setNewParent((p) => ({ ...p, password: pwd }));
  };

  const handleCreateParent = async () => {
    if (!newParent.name.trim() || !newParent.email.trim()) {
      toast.error("Parent name and email are required.");
      return;
    }
    const parentRole = roles.find((r) => r.name === "Parent");
    setSavingParent(true);
    try {
      const resp = await quickCreateParent({
        name: newParent.name.trim(),
        email: newParent.email.trim(),
        password: newParent.password,
        roleId: parentRole?.id, // server falls back to Parent role if omitted
      });
      const createdUser = resp?.data;
      await fetchParentUsers();
      if (createdUser?.id) {
        setStudentData((p) => ({ ...p, [parentTarget]: createdUser.id }));
      }
      setShowParentDialog(false);
      toast.success(resp?.message || "Parent created and selected.");
    } catch (err) {
      console.error("handleCreateParent error:", err?.response?.data || err);
      toast.error(err?.response?.data?.message || "Failed to create parent.");
    } finally {
      setSavingParent(false);
    }
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const resp = await getStudents();
      const students = Array.isArray(resp) ? resp : resp.data || [];

      const formatted = students.map((s) => {
        const gradeId =
          s.grade?.id ||
          s.gradeId ||
          s.grade_id ||
          (s.grade && typeof s.grade === "object" && s.grade.id) ||
          null;

        const gradeObj = grades.find((g) => g.id === gradeId);
        const gradeName = gradeObj?.name || (s.grade && s.grade.name) || "";

        const enrollment =
          (s.enrollments && s.enrollments.find((e) => e.status === "active")) ||
          (s.enrollments && s.enrollments[0]) ||
          null;

        let subject_ids = [];
        if (enrollment && enrollment.curriculumSubject) {
          subject_ids = [enrollment.curriculumSubject.id];
        } else if (enrollment && enrollment.subjects && enrollment.subjects.length > 0) {
          subject_ids = enrollment.subjects.map(subj => subj.id || subj.curriculum_subject_id);
        }

        const enrollmentNo = enrollment?.enrollment_no || 
                            (s.enrollments && s.enrollments[0]?.enrollment_no) || 
                            "Not Assigned";

        return {
          ...s,
          grade: gradeName,
          gradeId,
          enrollment_no: enrollmentNo,
          enrollment_status: enrollment?.status || "active",
          enrollment_date: enrollment?.enrolled_at || new Date(),
          enrollment_subject_ids: subject_ids,
          base_fee: Number(s.base_fee || 0),
          transport_fee: Number(s.transport_fee || 0),
          discount: Number(s.discount || 0),
          total_fee:
            Number(s.base_fee || 0) +
            Number(s.transport_fee || 0) -
            Number(s.discount || 0),
        };
      });

      setStudentsData(formatted);
    } catch (err) {
      console.error("fetchStudents error:", err);
      setStudentsData([]);
    } finally {
      setLoading(false);
    }
  };

  const loadEnrollments = async (studentId) => {
    if (!studentId) {
      setEnrollmentsList([]);
      setCurrentStudentId(null);
      return;
    }
    try {
      // Use the new enrollment history endpoint
      const historyData = await getStudentEnrollmentHistory(studentId);
      const enrollmentHistory = historyData?.enrollment_history || [];

      const normalized = enrollmentHistory.map((e) => {
        let subject_ids = [];
        if (e.subjects && e.subjects.length > 0) {
          subject_ids = e.subjects.map(subj => subj.id || subj.curriculum_subject_id);
        }

        return {
          id: e.id,
          enrollment_no: e.enrollment_no || "Not Assigned",
          grade_id: e.grade_id || e.grade?.id,
          status: e.status,
          waitlist_position: e.waitlist_position,
          enrolled_at: e.enrolled_at || null,
          subject_ids: subject_ids,
          session_id: e.session_id || e.session?.id || 1,
          grade_name: e.grade?.name || grades.find(g => g.id === e.grade_id)?.name || "",
          session_name: e.session?.name || "",
          subjects_list: e.subjects ? e.subjects.map(s => s.name).join(', ') : '',
          student_id: studentId,
        };
      });

      setEnrollmentsList(normalized);
      setCurrentStudentId(studentId);
    } catch (err) {
      console.warn("loadEnrollments failed:", err);
      setEnrollmentsList([]);
      setCurrentStudentId(null);
    }
  };

  const loadSubjects = async (gradeId, selected = []) => {
    if (!gradeId) {
      setSubjects([]);
      setMandatorySubjects([]);
      setElectiveSubjects([]);
      setEnrollmentData((p) => ({ ...p, subject_ids: [] }));
      return;
    }
    try {
      const data = await getSubjectsByGrade(gradeId);
      const subjectsData = Array.isArray(data) ? data : data.data || [];
      
      const mandatory = subjectsData.filter((s) => s.is_mandatory);
      const elective = subjectsData.filter((s) => !s.is_mandatory);
      setSubjects(subjectsData);
      setMandatorySubjects(mandatory);
      setElectiveSubjects(elective);

      setEnrollmentData((prev) => ({
        ...prev,
        grade_id: gradeId,
        subject_ids: [
          ...mandatory.map((s) => s.id),
          ...selected.filter((id) => elective.some((es) => es.id === id)),
        ],
      }));
    } catch (err) {
      console.error("loadSubjects error:", err);
      setSubjects([]);
      setMandatorySubjects([]);
      setElectiveSubjects([]);
    }
  };

  // ---------- CREATE / EDIT ----------
  const handleCreate = () => {
    setIsEditing(false);
    setActiveTabIndex(0);
    setErrors({});
    setStudentData({
      full_name: "",
      gender: "",
      birth_date: null,
      base_fee: 0,
      transport_fee: 0,
      discount: 0,
      parent_id: isParent ? user?.id : null,
      guardian2_id: null,
      grade_id: null, // For display only - actual grade set via enrollment
    });
    setEnrollmentData({
      id: null,
      enrollment_no: null,
      grade_id: null,
      subject_ids: [],
      status: "active",
      enrolled_at: new Date(),
      session_id: 1,
    });
    setEnrollmentsList([]);
    setCurrentStudentId(null);
    setSubjects([]);
    setMandatorySubjects([]);
    setElectiveSubjects([]);
    setUserFields({ name: "", email: "", password: "", roleId: null });
    setCreateUser(false);
    setPhotoFile(null);
    setPhotoPreview(null);
    setEditingEnrollmentNo(null);
    setIsModalVisible(true);
  };

  const handleEdit = async (row) => {
    setIsEditing(true);
    setActiveTabIndex(0);
    setErrors({});
    // Grade is derived from active enrollment, not stored on student
    const gradeId = row.gradeId || row.grade_id || (row.current_enrollment?.grade?.id) || null;

    setStudentData({
      id: row.id,
      full_name: row.full_name,
      gender: row.gender,
      birth_date: row.birth_date ? new Date(row.birth_date) : null,
      base_fee: row.base_fee || 0,
      transport_fee: row.transport_fee || 0,
      discount: row.discount || 0,
      parent_id: row.parent_id || null,
      guardian2_id: row.guardian2_id || null,
      grade_id: gradeId, // For display only - derived from active enrollment
      enrollment_no: row.enrollment_no || null, // shown in dialog header
      photo: row.photo || null,
    });
    setPhotoFile(null);
    setPhotoPreview(row.photo || null);
    setEditingEnrollmentNo(null);

    try {
      await loadEnrollments(row.id);
    } catch (err) {
      console.warn("Could not load enrollments, but continuing...");
      setEnrollmentsList([]);
      setCurrentStudentId(null);
    }

    setEnrollmentData({
      id: null,
      enrollment_no: null,
      grade_id: null,
      subject_ids: [],
      status: "active",
      enrolled_at: new Date(),
      session_id: 1,
    });

    setSubjects([]);
    setMandatorySubjects([]);
    setElectiveSubjects([]);

    setIsModalVisible(true);
  };

  const handleGradeChange = async (gradeId) => {
    // Grade is stored on enrollment, not student
    // Update studentData.grade_id for UI display only
    setStudentData((p) => ({ ...p, grade_id: gradeId }));

    // Update enrollment with the new grade
    setEnrollmentData((p) => ({
      ...p,
      grade_id: gradeId,
      subject_ids: [],
    }));
    await loadSubjects(gradeId);
  };

  // ---------- VALIDATION ----------
  // Returns a map of { fieldKey: message }. Also used to figure out which tab to jump to.
  const validate = () => {
    const errs = {};
    if (!studentData.full_name || !studentData.full_name.trim()) {
      errs.full_name = "Full name is required.";
    }
    if (!studentData.grade_id) {
      errs.grade_id = "Grade is required.";
    }
    if (!studentData.gender) {
      errs.gender = "Gender is required.";
    }
    if (studentData.birth_date) {
      const bd = new Date(studentData.birth_date);
      if (bd > maxBirthDate) {
        errs.birth_date = `Student must be at least ${MIN_ADMISSION_AGE} years old.`;
      } else if (bd < minBirthDate) {
        errs.birth_date = "Birth date looks too far in the past.";
      }
    }
    if (enrollmentData.grade_id && enrollmentData.subject_ids.length === 0) {
      errs.subjects = "Please select subjects for the enrollment.";
    }
    return errs;
  };

  // Fields on each tab, so we can auto-switch to the first tab with an error.
  const TAB_FIELDS = [
    ["full_name", "grade_id", "gender", "birth_date"], // Student Info
    [], // Fee Info
    ["subjects"], // Enrollment Info
  ];

  // ---------- SAVE ----------
  const handleSubmit = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      const firstTab = TAB_FIELDS.findIndex((fields) =>
        fields.some((f) => errs[f])
      );
      if (firstTab >= 0) setActiveTabIndex(firstTab);
      toast.error("Please fix the highlighted fields before saving.");
      return;
    }

    setLoading(true);
    try {
      // NOTE: grade is derived from enrollment, not stored on student
      const studentPayload = {
        full_name: studentData.full_name,
        gender: studentData.gender,
        birth_date: studentData.birth_date,
        base_fee: Number(studentData.base_fee) || 0,
        transport_fee: Number(studentData.transport_fee) || 0,
        discount: Number(studentData.discount) || 0,
        parent_id: studentData.parent_id,
        guardian2_id: studentData.guardian2_id || null,
      };

      const enrollmentPayload = {
        grade_id: enrollmentData.grade_id,
        subject_ids: enrollmentData.subject_ids,
        status: enrollmentData.status,
        enrolled_at: enrollmentData.enrolled_at || new Date(),
        session_id: enrollmentData.session_id || 1,
      };

      if (enrollmentData.id) {
        enrollmentPayload.id = enrollmentData.id;
      }

      if (isEditing) {
        const payloadToSend = { ...studentPayload };
        if (enrollmentData.grade_id && enrollmentData.subject_ids.length > 0) {
          payloadToSend.enrollment = enrollmentPayload;
        }

        const resp = await editStudent(studentData.id, payloadToSend);
        await maybeUploadPhoto(studentData.id);

        // Grade comes from enrollment, update display value
        setStudentData(prev => ({
          ...prev,
          grade_id: enrollmentData.grade_id || prev.grade_id,
        }));

        await fetchStudents();
        setIsModalVisible(false);
        toast.success("Student updated successfully");
      } else {
        if (createUser) {
          const payloadToSend = { ...userFields, ...studentPayload };
          if (enrollmentData.grade_id && enrollmentData.subject_ids.length > 0) {
            payloadToSend.enrollment = enrollmentPayload;
          }
          const resp = await addUserWithStudent(payloadToSend);
          await maybeUploadPhoto(resp?.student?.id || resp?.data?.student?.id || resp?.data?.id);
          await fetchStudents();
          setIsModalVisible(false);
          toast.success("Student created successfully");
        } else {
          const payloadToSend = { ...studentPayload };
          if (enrollmentData.grade_id && enrollmentData.subject_ids.length > 0) {
            payloadToSend.enrollment = enrollmentPayload;
          }
          const resp = await addStudent(payloadToSend);
          await maybeUploadPhoto(resp?.data?.id);
          await fetchStudents();
          setIsModalVisible(false);
          toast.success("Student created successfully");
        }
      }
    } catch (err) {
      console.error("handleSubmit error:", err?.response?.data || err);
      const msg = err?.response?.data?.message || err?.message || "Failed to save student/enrollment";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ---------- ENROLLMENT ACTIONS ----------
  const handleEditEnrollment = async (enrollment) => {
    await loadSubjects(enrollment.grade_id, enrollment.subject_ids || []);
    
    setEnrollmentData({
      id: enrollment.id,
      enrollment_no: enrollment.enrollment_no,
      grade_id: enrollment.grade_id,
      subject_ids: enrollment.subject_ids || [],
      status: enrollment.status,
      enrolled_at: enrollment.enrolled_at ? new Date(enrollment.enrolled_at) : new Date(),
      session_id: enrollment.session_id || 1,
    });
    setEditingEnrollmentNo(enrollment.enrollment_no);
    // Draw the eye to the form below the history table
    setTimeout(() => enrollmentFormRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
  };

  const handleViewEnrollment = (enrollment) => {
    setViewEnrollment(enrollment);
  };

  const handlePrintEnrollment = async (row) => {
    const ok = await printEnrollmentSlip({
      studentName: studentData.full_name,
      photo: studentData.photo,
      enrollmentNo: row.enrollment_no,
      sessionName: row.session_name,
      gradeName: row.grade_name,
      subjects: row.subjects_list,
      status: row.status,
      enrolledAt: formatDate(row.enrolled_at),
    });
    if (!ok) toast.error("Pop-up blocked. Allow pop-ups to print.");
  };

  // **FIXED: Simple static action buttons without three dots menu**
  const enrollmentActionBodyTemplate = (rowData) => {
    const isActive = rowData.status === "active";
    const isCurrentStudentEnrollment = rowData.student_id === currentStudentId;
    const canEdit = isActive && isCurrentStudentEnrollment;

    const smallBtn = { width: "1.75rem", height: "1.75rem", padding: 0 };

    return (
      <div className="flex gap-1 justify-content-center">
        {/* Edit Button - Only enabled for active enrollments of current student */}
        <Button
          icon="pi pi-pencil"
          style={smallBtn}
          className={`p-button-rounded p-button-text ${
            canEdit ? "p-button-success" : "p-button-secondary"
          }`}
          tooltip={
            canEdit
              ? "Edit Enrollment"
              : !isActive
                ? "Cannot edit completed/cancelled enrollment"
                : "Cannot edit other student's enrollment"
          }
          onClick={() => handleEditEnrollment(rowData)}
          disabled={!canEdit}
        />

        {/* View Button - Always enabled */}
        <Button
          icon="pi pi-eye"
          style={smallBtn}
          className="p-button-rounded p-button-text p-button-info"
          tooltip="View Enrollment Details"
          onClick={() => handleViewEnrollment(rowData)}
        />

        {/* Print Button - Always enabled */}
        <Button
          icon="pi pi-print"
          style={smallBtn}
          className="p-button-rounded p-button-text p-button-secondary"
          tooltip="Print Enrollment Slip"
          onClick={() => handlePrintEnrollment(rowData)}
        />
      </div>
    );
  };

  const handleDelete = async (studentId) => {
    try {
      await deleteStudent(studentId);
      await fetchStudents();
      toast.success("Deleted successfully");
    } catch (err) {
      console.error("handleDelete error:", err);
      toast.error("Failed to delete student");
    }
  };

  const isStatusEditable = () => enrollmentData.status === "active";

  const subjectOptions = [
    {
      label: "Mandatory Subjects",
      items: mandatorySubjects.map((s) => ({ label: s.name, value: s.id, disabled: true })),
    },
    {
      label: "Elective Subjects",
      items: electiveSubjects.map((s) => ({ label: s.name, value: s.id })),
    },
  ];

  // **STUDENT COLUMNS**
  const studentColumns = [
    {
      field: "full_name",
      header: "Full Name",
      body: (row) => (
        <div className="flex align-items-center gap-2">
          {row.photo ? (
            <img
              src={row.photo}
              alt=""
              className="border-circle"
              style={{ width: 32, height: 32, objectFit: "cover" }}
            />
          ) : (
            <Avatar icon="pi pi-user" shape="circle" style={{ width: 32, height: 32 }} />
          )}
          <span>{row.full_name}</span>
        </div>
      ),
    },
    { field: "enrollment_no", header: "Enrollment #" },
    { field: "grade", header: "Grade" },
    {
      field: "parent_id",
      header: "Guardian(s)",
      body: (row) => {
        const guardians = [
          row.parent ? { ...row.parent, label: "G1" } : null,
          row.guardian2 ? { ...row.guardian2, label: "G2" } : null,
        ].filter(Boolean);
        if (guardians.length === 0) return <span className="text-color-secondary">—</span>;
        return (
          <div className="flex flex-column gap-1">
            {guardians.map((g) => (
              <div key={g.id} className="flex flex-column">
                <span className="font-medium text-sm">{g.name}</span>
                {g.email && <span className="text-color-secondary text-xs">{g.email}</span>}
              </div>
            ))}
          </div>
        );
      },
    },
    { field: "base_fee", header: "Base Fee" },
    { field: "transport_fee", header: "Transport Fee" },
    { field: "discount", header: "Discount" },
  ];

  // Enrollment columns with session info
  const enrollmentColumns = [
    { field: "session_name", header: "Session", sortable: true },
    { field: "grade_name", header: "Grade", sortable: true },
    { field: "enrollment_no", header: "Enrollment #", sortable: true },
    { field: "status", header: "Status", sortable: true },
    {
      field: "enrolled_at",
      header: "Enrolled",
      sortable: true,
      body: (rowData) => formatDate(rowData.enrolled_at)
    },
    {
      field: 'actions',
      header: 'Actions',
      body: enrollmentActionBodyTemplate,
      style: { width: '120px', textAlign: 'center' }
    }
  ];

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div>
      <DynamicDataTable
        data={studentsData || []}
        subject={isParent ? "Children" : "Student"}
        columns={studentColumns}
        onCreate={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <Dialog
        header={
          isEditing
            ? `Edit Student — ${studentData.full_name || ""}${
                studentData.enrollment_no &&
                studentData.enrollment_no !== "Not Assigned"
                  ? ` (#${studentData.enrollment_no})`
                  : ""
              }`
            : "Add New Student"
        }
        visible={isModalVisible}
        style={{ width: "80vw", maxWidth: "1100px" }}
        onHide={() => setIsModalVisible(false)}
      >
        <TabView
          activeIndex={activeTabIndex}
          onTabChange={(e) => setActiveTabIndex(e.index)}
        >
          {/* Student Info */}
          <TabPanel header="Student Info">
            <div className="p-fluid formgrid grid" style={{ minHeight: TAB_CONTENT_HEIGHT, alignContent: "flex-start" }}>
              {/* Student photo: upload or camera */}
              <div className="field col-12 flex align-items-center gap-3">
                <div style={{ position: "relative", width: 90, height: 90 }}>
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt="Student"
                      className="border-circle shadow-1"
                      style={{ width: 90, height: 90, objectFit: "cover" }}
                    />
                  ) : (
                    <Avatar
                      icon="pi pi-user"
                      shape="circle"
                      style={{ width: 90, height: 90, fontSize: "2rem" }}
                    />
                  )}
                </div>
                <div className="flex flex-column gap-2" style={{ width: "auto" }}>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      icon="pi pi-upload"
                      label="Upload Photo"
                      className="p-button-outlined p-button-sm"
                      style={{ width: "auto" }}
                      onClick={() => photoInputRef.current?.click()}
                    />
                    <Button
                      type="button"
                      icon="pi pi-camera"
                      label="Take Photo"
                      className="p-button-outlined p-button-sm"
                      style={{ width: "auto" }}
                      onClick={openCamera}
                    />
                    {photoPreview && (
                      <Button
                        type="button"
                        icon="pi pi-times"
                        className="p-button-text p-button-danger p-button-sm"
                        style={{ width: "auto" }}
                        tooltip="Remove selected photo"
                        onClick={() => setPendingPhoto(null)}
                      />
                    )}
                  </div>
                  <small className="text-color-secondary">
                    Optional. Saved with the student when you press {isEditing ? "Update" : "Save"}.
                  </small>
                </div>
                <input
                  type="file"
                  ref={photoInputRef}
                  style={{ display: "none" }}
                  accept="image/*"
                  onChange={(e) => setPendingPhoto(e.target.files?.[0] || null)}
                />
              </div>

              <div className="field col-12">
                <label>Full Name *</label>
                <InputText
                  className={errors.full_name ? "p-invalid" : ""}
                  value={studentData.full_name}
                  onChange={(e) => {
                    const v = e.target.value;
                    setStudentData((p) => ({ ...p, full_name: v }));
                    if (errors.full_name) setErrors((er) => ({ ...er, full_name: undefined }));
                  }}
                />
                {errors.full_name && <small className="p-error">{errors.full_name}</small>}
              </div>
              <div className="field col-12 md:col-6">
                <label>Grade *</label>
                <Dropdown
                  className={errors.grade_id ? "p-invalid" : ""}
                  value={studentData.grade_id}
                  options={[{ label: "Select Grade", value: null }, ...grades.map((g) => ({ label: g.name, value: g.id }))]}
                  onChange={(e) => {
                    handleGradeChange(e.value);
                    if (errors.grade_id) setErrors((er) => ({ ...er, grade_id: undefined }));
                  }}
                  placeholder="Select Grade"
                />
                {errors.grade_id && <small className="p-error">{errors.grade_id}</small>}
              </div>
              <div className="field col-12 md:col-6">
                <label>Gender *</label>
                <Dropdown
                  className={errors.gender ? "p-invalid" : ""}
                  value={studentData.gender}
                  options={[
                    { label: "Select Gender", value: null },
                    { label: "Male", value: "Male" },
                    { label: "Female", value: "Female" },
                    { label: "Other", value: "Other" },
                  ]}
                  onChange={(e) => {
                    setStudentData((p) => ({ ...p, gender: e.value }));
                    if (errors.gender) setErrors((er) => ({ ...er, gender: undefined }));
                  }}
                  placeholder="Select Gender"
                />
                {errors.gender && <small className="p-error">{errors.gender}</small>}
              </div>
              <div className="field col-12 md:col-6">
                <label>Birth Date</label>
                <Calendar
                  className={errors.birth_date ? "p-invalid" : ""}
                  value={studentData.birth_date}
                  onChange={(e) => {
                    setStudentData((p) => ({ ...p, birth_date: e.value }));
                    if (errors.birth_date) setErrors((er) => ({ ...er, birth_date: undefined }));
                  }}
                  minDate={minBirthDate}
                  maxDate={maxBirthDate}
                  showIcon
                  dateFormat="mm/dd/yy"
                />
                {errors.birth_date && <small className="p-error">{errors.birth_date}</small>}
              </div>
              {!isParent && (
                <>
                  <div className="field col-12 md:col-6">
                    <label>Guardian 1 (Parent)</label>
                    <div className="p-inputgroup">
                      <Dropdown
                        value={studentData.parent_id}
                        options={[
                          { label: "— None —", value: null },
                          ...parentUsers.map((u) => ({ label: `${u.name} (${u.email})`, value: u.id })),
                        ]}
                        onChange={(e) => setStudentData((p) => ({ ...p, parent_id: e.value }))}
                        placeholder="Select parent"
                        filter
                        showClear
                      />
                      <Button
                        type="button"
                        icon="pi pi-plus"
                        tooltip="Create new parent"
                        onClick={() => openParentDialog("parent_id")}
                      />
                    </div>
                  </div>
                  <div className="field col-12 md:col-6">
                    <label>Guardian 2 (Optional)</label>
                    <div className="p-inputgroup">
                      <Dropdown
                        value={studentData.guardian2_id}
                        options={[
                          { label: "— None —", value: null },
                          ...parentUsers
                            .filter((u) => u.id !== studentData.parent_id)
                            .map((u) => ({ label: `${u.name} (${u.email})`, value: u.id })),
                        ]}
                        onChange={(e) => setStudentData((p) => ({ ...p, guardian2_id: e.value }))}
                        placeholder="Select second guardian"
                        filter
                        showClear
                      />
                      <Button
                        type="button"
                        icon="pi pi-plus"
                        tooltip="Create new parent"
                        onClick={() => openParentDialog("guardian2_id")}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </TabPanel>

          {/* Fee Info */}
          <TabPanel header="Fee">
            <div className="p-fluid formgrid grid" style={{ minHeight: TAB_CONTENT_HEIGHT, alignContent: "flex-start" }}>
              <div className="field col-12 md:col-4">
                <label>Base Fee</label>
                <InputText
                  type="number"
                  value={studentData.base_fee}
                  onChange={(e) => setStudentData((p) => ({ ...p, base_fee: e.target.value }))}
                />
              </div>
              <div className="field col-12 md:col-4">
                <label>Transport Fee</label>
                <InputText
                  type="number"
                  value={studentData.transport_fee}
                  onChange={(e) => setStudentData((p) => ({ ...p, transport_fee: e.target.value }))}
                />
              </div>
              <div className="field col-12 md:col-4">
                <label>Discount</label>
                <InputText
                  type="number"
                  value={studentData.discount}
                  onChange={(e) => setStudentData((p) => ({ ...p, discount: e.target.value }))}
                />
              </div>
              <div className="field col-12 md:col-4">
                <label>Total Fee</label>
                <InputText
                  readOnly
                  value={Number(studentData.base_fee || 0) + Number(studentData.transport_fee || 0) - Number(studentData.discount || 0)}
                />
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Enrollment">
            {/* Edit mode: enrollment history on top, form below */}
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem", minHeight: TAB_CONTENT_HEIGHT }}>
              {isEditing && (
                <div className="card" style={{ width: "100%" }}>
                  <h4 style={{ marginBottom: "1rem" }}>All Enrollments</h4>
                  <DataTable
                    value={enrollmentsList}
                    scrollable
                    scrollHeight="190px"
                    emptyMessage="No enrollments found for this student"
                    className="p-datatable-sm"
                  >
                    <Column field="session_name" header="Session" sortable style={{ minWidth: '100px' }} />
                    <Column field="grade_name" header="Grade" sortable style={{ minWidth: '80px' }} />
                    <Column field="enrollment_no" header="Enrollment #" sortable style={{ minWidth: '120px' }} />
                    <Column field="status" header="Status" sortable style={{ minWidth: '80px' }} />
                    <Column
                      field="enrolled_at"
                      header="Enrolled"
                      sortable
                      body={(rowData) => formatDate(rowData.enrolled_at)}
                      style={{ minWidth: '100px' }}
                    />
                    <Column
                      header="Actions"
                      body={enrollmentActionBodyTemplate}
                      style={{ width: '140px', textAlign: 'center' }}
                    />
                  </DataTable>
                </div>
              )}

              {/* Enrollment Form */}
              <div
                ref={enrollmentFormRef}
                className="card"
                style={{
                  width: "100%",
                  transition: "box-shadow 0.4s, border-color 0.4s",
                  ...(editingEnrollmentNo
                    ? { border: "2px solid var(--primary-color)", boxShadow: "0 0 0 3px var(--primary-100, rgba(99,102,241,0.25))" }
                    : {}),
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                  <h4 style={{ margin: 0 }}>
                    {editingEnrollmentNo ? (
                      <span className="text-primary">
                        <i className="pi pi-pencil mr-2" />
                        Editing enrollment {editingEnrollmentNo}
                      </span>
                    ) : isEditing ? "Enrollment Details" : "Enrollment Form"}
                  </h4>
                </div>
                <div className="p-fluid formgrid grid">
                    <div className="field col-12 md:col-6">
                      <label>Enrollment Date</label>
                      <Calendar
                        value={enrollmentData.enrolled_at}
                        onChange={(e) => setEnrollmentData((p) => ({ ...p, enrolled_at: e.value }))}
                        showIcon
                        disabled={!isStatusEditable()}
                      />
                    </div>

                    <div className="field col-12 md:col-6">
                      <label>Status</label>
                      <Dropdown
                        value={enrollmentData.status}
                        options={[
                          { label: "Active", value: "active" },
                          { label: "Completed", value: "completed" },
                          { label: "Cancelled", value: "cancelled" },
                        ]}
                        onChange={(e) => setEnrollmentData((p) => ({ ...p, status: e.value }))}
                        disabled={!isStatusEditable()}
                      />
                    </div>

                    <div className="field col-12 md:col-3">
                      <label>Grade *</label>
                      <Dropdown
                        value={enrollmentData.grade_id}
                        options={[{ label: "Select Grade", value: null }, ...grades.map((g) => ({ label: g.name, value: g.id }))]}
                        onChange={(e) => handleGradeChange(e.value)}
                        placeholder="Select Grade"
                        disabled={!isStatusEditable()}
                      />
                    </div>

                    <div className="field col-12 md:col-9">
                      <label>Subjects *</label>
                      <MultiSelect
                        className={errors.subjects ? "p-invalid" : ""}
                        value={enrollmentData.subject_ids}
                        options={subjectOptions}
                        optionGroupLabel="label"
                        optionGroupChildren="items"
                        display="chip"
                        onChange={(e) => {
                          const selectedElectives = e.value.filter((id) =>
                            electiveSubjects.some((s) => s.id === id)
                          );
                          const allSelected = [
                            ...mandatorySubjects.map((s) => s.id),
                            ...selectedElectives,
                          ];
                          setEnrollmentData((p) => ({ ...p, subject_ids: allSelected }));
                          if (errors.subjects) setErrors((er) => ({ ...er, subjects: undefined }));
                        }}
                        placeholder="Select Subjects"
                        disabled={!enrollmentData.grade_id || !isStatusEditable()}
                      />
                      {errors.subjects && <small className="p-error">{errors.subjects}</small>}
                    </div>
                </div>
              </div>

            </div>
          </TabPanel>
        </TabView>

        <div className="p-d-flex p-jc-end p-mt-3">
          <Button
            label="Cancel"
            className="p-button-text p-mr-2"
            onClick={() => setIsModalVisible(false)}
          />
          <Button
            label={isEditing ? "Update" : "Save"}
            className="p-button-primary"
            onClick={handleSubmit}
            disabled={loading}
          />
        </div>

        {loading && <ProgressSpinner style={{ width: "50px", height: "50px" }} />}
      </Dialog>

      {/* Create parent on the go */}
      <Dialog
        header="New Parent / Guardian"
        visible={showParentDialog}
        style={{ width: "420px" }}
        onHide={() => setShowParentDialog(false)}
      >
        <div className="p-fluid formgrid grid">
          <div className="field col-12">
            <label>Name *</label>
            <InputText
              value={newParent.name}
              onChange={(e) => setNewParent((p) => ({ ...p, name: e.target.value }))}
            />
          </div>
          <div className="field col-12">
            <label>Email *</label>
            <InputText
              type="email"
              value={newParent.email}
              onChange={(e) => setNewParent((p) => ({ ...p, email: e.target.value }))}
            />
          </div>
          <div className="field col-12">
            <label>Password</label>
            <div className="p-inputgroup">
              <InputText
                type="text"
                value={newParent.password}
                placeholder="Leave blank to auto-generate"
                onChange={(e) => setNewParent((p) => ({ ...p, password: e.target.value }))}
              />
              <Button
                type="button"
                icon="pi pi-refresh"
                label="Generate"
                onClick={generatePassword}
              />
            </div>
            <small className="text-color-secondary">
              Optional. The account is created verified &amp; active, and a
              password-reset email is sent so the parent can set their own password.
              If left blank, a secure password is generated automatically.
            </small>
          </div>
        </div>
        <div className="flex justify-content-end gap-2 mt-2">
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => setShowParentDialog(false)}
            disabled={savingParent}
          />
          <Button
            label="Create & Select"
            icon="pi pi-check"
            onClick={handleCreateParent}
            loading={savingParent}
          />
        </div>
      </Dialog>

      {/* Camera capture */}
      <Dialog
        header="Take Photo"
        visible={isCameraOpen}
        style={{ width: "480px", maxWidth: "95vw" }}
        onHide={() => setIsCameraOpen(false)}
      >
        <div className="flex flex-column align-items-center gap-3">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: "100%", borderRadius: 8, background: "#000" }}
          />
          <div className="flex gap-2">
            <Button label="Capture" icon="pi pi-camera" onClick={capturePhoto} />
            <Button label="Cancel" className="p-button-outlined" onClick={() => setIsCameraOpen(false)} />
          </div>
        </div>
      </Dialog>

      {/* Read-only enrollment details */}
      <Dialog
        header={`Enrollment ${viewEnrollment?.enrollment_no || ""}`}
        visible={!!viewEnrollment}
        style={{ width: "480px", maxWidth: "95vw" }}
        onHide={() => setViewEnrollment(null)}
      >
        {viewEnrollment && (
          <div className="p-fluid">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {[
                  ["Enrollment #", viewEnrollment.enrollment_no],
                  ["Session", viewEnrollment.session_name || "-"],
                  ["Grade", viewEnrollment.grade_name || "-"],
                  ["Subjects", viewEnrollment.subjects_list || "-"],
                  ["Status", viewEnrollment.status],
                  ["Enrolled", formatDate(viewEnrollment.enrolled_at)],
                  ...(viewEnrollment.waitlist_position
                    ? [["Waitlist Position", viewEnrollment.waitlist_position]]
                    : []),
                ].map(([label, value]) => (
                  <tr key={label} style={{ borderBottom: "1px solid var(--surface-border)" }}>
                    <td className="font-semibold py-2" style={{ width: "40%" }}>{label}</td>
                    <td className="py-2" style={{ textTransform: label === "Status" ? "capitalize" : "none" }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-content-end gap-2 mt-3">
              <Button
                label="Print"
                icon="pi pi-print"
                className="p-button-outlined"
                onClick={() => handlePrintEnrollment(viewEnrollment)}
              />
              <Button label="Close" className="p-button-text" onClick={() => setViewEnrollment(null)} />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default StudentList;